import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Spin, Result, Button } from "antd";
import { assetService } from "@iso/services";
import AssetViewSummary from "./AssetViewSummary";

/**
 * Scan-to-passport landing page. A printed asset QR encodes
 * /asset-passport/:token; scanning it with any phone camera lands here. This is
 * a private route, so an unauthenticated scan is bounced through login first and
 * returns here afterwards — then we resolve the token to its asset and open the
 * live passport. Access is enforced server-side (property-scoped resolve-qr), so
 * a stray label can't surface an asset the viewer isn't entitled to.
 */
export default function AssetPassport() {
  const { token } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [asset, setAsset] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    assetService
      .resolveQr(token)
      .then((res) => {
        if (!active) return;
        if (res && res.code === "0000" && res.asset) {
          setAsset(res.asset);
        } else {
          setError((res && res.message) || "This QR code is not linked to an asset you can access.");
        }
      })
      .catch(() => {
        if (active) setError("Unable to load this asset. Please try again.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" tip="Loading asset passport…" />
      </div>
    );
  }

  if (error || !asset) {
    return (
      <Result
        status="warning"
        title="Asset not found"
        subTitle={error || "This QR code is not linked to an asset you can access."}
        extra={
          <Button type="primary" onClick={() => history.push("/manage-assets")}>
            Go to Assets
          </Button>
        }
      />
    );
  }

  return (
    <AssetViewSummary
      visibleViewSummary={true}
      setVisibleViewSummary={() => history.push("/manage-assets")}
      viewSummary={asset}
      defaultTab="details"
    />
  );
}
